import React from 'react';
import { Box, IconButton, Typography, AppBar, Toolbar } from '@mui/material';
import { styled } from '@mui/system';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import { SearchBar, SearchBarProps } from 'components/SearchBar/SearchBar';
import { useLayoutElements } from 'contexts/LayoutElementsContext/useLayoutElements';

export interface PageHeaderProps {
  title: string;
  searchPlaceholder?: string;
  showSearch?: boolean;
  isBackVisible?: boolean;
  onBack?: () => void;
  onSearchIconClick?: () => void;
  onSearchClick?: () => void;
  autoFocusSearch?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onSearchSubmit?: (value: string) => void;
  onClear?: () => void;
}

const Header = styled(AppBar)(({ theme }) => ({
  position: 'sticky',
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
  boxShadow: `0 ${theme.spacing(0.5)} ${theme.spacing(1)} 0 rgba(0, 0, 0, 0.10)`,
  [theme.breakpoints.up('md')]: {
    position: 'relative',
  },
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  height: theme.spacing(7),
  justifyContent: 'space-between',
  [theme.breakpoints.up('sm')]: {
    height: theme.spacing(8),
  },
}));

const HeaderLeft = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flex: 1,
  minWidth: 0,
}));

const CompactSearchBar = styled(Box)(({ theme }) => ({
  display: 'none',
  width: theme.spacing(35),
  [theme.breakpoints.up('sm')]: {
    display: 'flex',
  },
}));

const SearchIconButton = styled(IconButton)(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    display: 'none',
  },
}));

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  searchPlaceholder = 'Search',
  showSearch = false,
  isBackVisible = false,
  onBack,
  onSearchIconClick,
  onSearchClick,
  autoFocusSearch = false,
  searchValue = '',
  onSearchChange,
  onSearchSubmit,
  onClear,
}) => {
  const { setPageHeaderRef } = useLayoutElements();

  return (
    <Header elevation={0} ref={setPageHeaderRef}>
      <StyledToolbar>
        <HeaderLeft>
          {isBackVisible && onBack && (
            <IconButton onClick={onBack} aria-label="Go back" sx={{ color: 'grey.800' }}>
              <ArrowBackIcon sx={{ fontSize: 24 }} />
            </IconButton>
          )}
          <Typography variant="title3" color="grey.800">
            {title}
          </Typography>
        </HeaderLeft>
        {showSearch && (
          <>
            <CompactSearchBar>
              <SearchBar
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={onSearchChange || (() => {})}
                onSubmit={onSearchSubmit}
                onClick={onSearchClick}
                onClear={onClear}
                autoFocus={autoFocusSearch}
              />
            </CompactSearchBar>
            <SearchIconButton
              onClick={onSearchIconClick}
              aria-label={`Search ${title.toLowerCase()}`}
              sx={{ color: 'grey.800' }}
            >
              <SearchIcon sx={{ fontSize: 24 }} />
            </SearchIconButton>
          </>
        )}
      </StyledToolbar>
    </Header>
  );
};
